import { ComponentProps } from 'react'
import type React from 'react'

import { theme } from '@factorialco/deprecated-design-system'
import { OnePersonListItem } from '@factorialco/f0-react/dist/experimental'
import {
  Bell as BellIcon,
  Check as CheckIcon,
  Coffee as CoffeeIcon,
  Cross as CrossIcon,
  PauseCircle as PauseCircleIcon,
  Schedule as ScheduleIcon,
  Suitcase as SuitcaseIcon,
} from '@factorialco/f0-react/icons/app'
import { TeamStatusActivityQuery } from 'generated/resources/graphql'

import { convertDate, formatSecondsToHoursAndMinutes } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { getLocationTypes } from 'modules/dashboard/components/Widgets/ClockIn/constants'

type ItemData = TeamStatusActivityQuery['timeInsights']['clockInStatusesConnection']['nodes'][0]

export const getBottomTags = (data: ItemData) => {
  const tags: ComponentProps<typeof OnePersonListItem>['bottomTags'] = []

  if (data.status === 'clocked_in') {
    // adding location type tag
    const locationTypes = getLocationTypes()
    const locationType = locationTypes.find((type) => type.id === data.shift?.locationType)

    if (locationType) {
      tags.push({
        text: locationType.name,
        icon: locationType.icon,
      })
    }

    // adding project tag
    const projectRecord = data.shift?.projectManagementTimeRecordsConnection?.nodes[0]

    const projectName = projectRecord?.projectWorker?.imputableProject?.name
    if (projectName) {
      const subprojectName = projectRecord?.subproject?.name

      const project = `${subprojectName ? `${subprojectName} · ` : ''}${projectName}`

      if (project.length) {
        tags.push({
          text: project,
          icon: SuitcaseIcon,
        })
      }
    }
  }

  if (data.status === 'in_a_break') {
    const breakTypeName = data.shift?.timeSettingsBreakConfiguration?.name

    if (breakTypeName) {
      tags.push({
        text: breakTypeName,
        icon: CoffeeIcon,
      })
    }
  }

  return tags
}

export const getAvatarBadge = (status: ItemData['status']) => {
  if (status === 'missing_clock_in') {
    return {
      icon: CrossIcon,
      type: 'critical' as const,
    }
  }

  if (status === 'in_a_break') {
    return {
      icon: PauseCircleIcon,
      type: 'warning' as const,
    }
  }

  if (status === 'clocked_in') {
    return {
      icon: CheckIcon,
      type: 'positive' as const,
    }
  }
}

export const getDescription = (data: ItemData) => {
  if (data.status === 'missing_clock_in') {
    if (data.latenessInSeconds <= 0) {
      return i18n.t('dashboard.who_is_in.missing_clock_in_explainer')
    }

    return `${formatSecondsToHoursAndMinutes(data.latenessInSeconds)} ${i18n.t('common.late')}`
  }

  if (data.status === 'should_not_be_working') {
    const leavesConnection = data.employee?.leavesConnection

    if (!leavesConnection) return ''

    const leave = leavesConnection.nodes?.[0]

    if (!leave) return ''

    const startOn = convertDate(leave.startOn, 'backend_datetime', 'ui')
    const endOn = leave.finishOn ? convertDate(leave.finishOn, 'backend_datetime', 'ui') : undefined

    if (endOn) {
      return `${startOn} → ${endOn}`
    }

    return i18n.t('dashboard.new_upcoming_absences.no_end_date')
  }
}

type PersonListItemActions = NonNullable<ComponentProps<typeof OnePersonListItem>['actions']>

export const getActions = ({
  data,
  handleSchedule,
  handleSendReminder,
}: {
  data: ItemData
  handleSchedule: () => void
  handleSendReminder: (e?: React.MouseEvent) => void
}) => {
  let primaryAction: NonNullable<PersonListItemActions>['primary']
  let secondaryAction: NonNullable<PersonListItemActions>['secondary']

  if (
    data.status === 'missing_clock_in' ||
    data.status === 'in_a_break' ||
    data.status === 'clocked_in'
  ) {
    secondaryAction = {
      icon: ScheduleIcon,
      onClick: handleSchedule,
    }
  }

  if (data.status === 'missing_clock_in') {
    primaryAction = {
      icon: BellIcon,
      label: i18n.t('dashboard.who_is_in.reminder_modal.send_reminder'),
      onClick: (e?: React.MouseEvent) => {
        e?.stopPropagation()
        handleSendReminder()
      },
    }
  }

  return {
    primary: primaryAction,
    secondary: secondaryAction,
  }
}

export const getRightTag = (data: ItemData) => {
  if (data.status === 'should_not_be_working') {
    const leave = data.employee?.leavesConnection?.nodes?.[0]

    if (!leave || !leave.leaveTypeName) return

    const color = leave.leaveTypeColor ? `#${leave.leaveTypeColor}` : theme.colors.grey400

    return {
      text: leave.leaveTypeName,
      customColor: color,
    }
  }
}
